import { useAdAccountStore, useBrandStore } from 'stores';
import { type AdManager, PAGE_DEFAULT_SIZE, type PaginationSettings } from 'types/fbTable';
import { IntegrationType } from 'types/integrations';
import type { AdAccount } from 'types/stores/ad-account-store';
import prepareQueryString from 'utils/prepareQueryString';
import useHandleApi from './useHandleApi';

// TODO(Jenky): Refactor to use proxima sdk
const useAdManager = () => {
  const { loading, handleApi } = useHandleApi();
  const { handleApi: handleIntegrationsApi } = useHandleApi({
    apiDomain: import.meta.env.VITE_PROXIMA_INTEGRATIONS_API_URL
  });
  const adAccountStore = useAdAccountStore();
  const { brand } = useBrandStore();

  const retrieveAdAccountFromApi = async (brandId: string) => {
    const response = await handleIntegrationsApi({
      endpoint: `/brand/${brandId}/fb/adAccount/active`,
      skipErrorToaster: true
    });

    const adAccounts = (response.data || []) as AdAccount[];

    adAccountStore.updateAdAcountsList(adAccounts, IntegrationType.Facebook);

    const [activeAdAccount] = adAccounts;

    return activeAdAccount ?? null;
  };

  const getMetaAdAccount = async (opts?: { refresh?: boolean; brandId: string }) => {
    const { refresh = false, brandId = brand.brandId } = opts ?? {};
    if (refresh) adAccountStore.clear(IntegrationType.Facebook);
    else {
      const currentActiveAdAccount = adAccountStore.getActiveAdAccountFromList(
        IntegrationType.Facebook
      );
      if (currentActiveAdAccount) return currentActiveAdAccount;
    }

    const activeAdAccount = await retrieveAdAccountFromApi(brandId);

    if (activeAdAccount) adAccountStore.updateAdAccount(activeAdAccount, IntegrationType.Facebook);

    return activeAdAccount;
  };

  const getAdAccountBatch = async (brandIds: string[]) => {
    const searchQuery = new URLSearchParams();
    searchQuery.append('brandId', brandIds.join(','));

    return handleApi({
      endpoint: `/integration/fb/adAccount/batch?${searchQuery.toString()}`
    });
  };

  const refreshAdAccount = async ({ brandId }: { brandId: string }) =>
    getMetaAdAccount({ refresh: true, brandId });

  const getApiUrl = async (
    { adAccountId } = { adAccountId: adAccountStore.metaAdAccountsList.at(0)?.adAccountId }
  ) => `/brand/${brand.brandId}/fb/adAccount/${adAccountId}`;

  type HandleGetRequestOpts = ({ path: AdManager; url?: never } | { path?: never; url: string }) & {
    adAccountId?: string;
  };
  const handleGetRequest = async (
    getRequestOpts: HandleGetRequestOpts,
    paginationOpts: PaginationSettings
  ) => {
    const { path, url, adAccountId } = getRequestOpts;
    const { queryItems, ...restOpts } = paginationOpts;
    const queryString = prepareQueryString({
      pageSize: PAGE_DEFAULT_SIZE,
      pageNumber: 1,
      ...restOpts,
      ...queryItems
    });

    let endpoint = url;
    if (path) {
      const apiUrl = await getApiUrl({ adAccountId });
      endpoint = `${apiUrl}/${path}`;
    }

    return handleApi({ endpoint: `${endpoint}?${queryString}` });
  };

  const getCampaigns = async (paginationOpts: PaginationSettings, adAccountId: string) => {
    const response = await handleGetRequest({ path: 'campaign', adAccountId }, paginationOpts);
    return response;
  };

  const getAdSets = async (paginationOpts: PaginationSettings, adAccountId: string) => {
    const response = await handleGetRequest({ path: 'adset', adAccountId }, paginationOpts);
    return response;
  };

  const getAds = async (paginationOpts: PaginationSettings, adAccountId: string) => {
    const response = await handleGetRequest({ path: 'ad', adAccountId }, paginationOpts);
    return response;
  };

  const getPerformanceAds = async (paginationOpts: PaginationSettings, adAccountId: string) => {
    const response = await handleGetRequest({ url: '/fb/ads', adAccountId }, paginationOpts);
    return response;
  };

  const getLastUpdatedAt = async (accountId?: string) => {
    const { adAccountId } =
      (accountId ? { adAccountId: accountId } : await getMetaAdAccount()) ?? {};
    if (adAccountId === undefined) return '-';

    const response = await handleApi({
      endpoint: `/brand/${brand.brandId}/integration/facebook/state/${adAccountId}/last-update`,
      skipErrorToaster: true
    });

    // TODO(Jenky): Address this differently
    if (response.error) {
      return '-';
    }

    return response.data.lastUpdatedAt;
  };

  const disconnectIntegration = async (brandId: string, integrationId: string) =>
    handleIntegrationsApi({
      endpoint: `/brand/${brandId}/integration/${integrationId}`,
      method: 'DELETE'
    });

  return {
    loading,
    retrieveAdAccountFromApi,
    refreshAdAccount,
    getLastUpdatedAt,
    getAdAccountBatch,
    getMetaAdAccount,
    getPerformanceAds,
    getCampaigns,
    getAdSets,
    getAds,
    disconnectIntegration
  };
};

export default useAdManager;
